
import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface AddCompanyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCompanyAdded: (companyName: string) => void;
}

const AddCompanyDialog = ({ open, onOpenChange, onCompanyAdded }: AddCompanyDialogProps) => {
  const [companyName, setCompanyName] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleAddCompany = async () => {
    if (!companyName.trim()) {
      toast.error("Please enter a company name");
      return;
    }

    setIsLoading(true);
    try {
      // Check if company already exists via admin edge function (admin-only)
      const { data: resp, error: fnErr } = await supabase.functions.invoke('admin-users', {
        body: { action: 'company_exists', company_name: companyName.trim() },
      });
      if (fnErr) throw fnErr;
      if (resp?.error) throw new Error(resp.error);

      if (resp?.exists) {
        toast.error("Company name already exists");
        setIsLoading(false);
        return;
      }

      onCompanyAdded(companyName.trim());
      setCompanyName("");
      onOpenChange(false);
      toast.success("Company added successfully");
    } catch (error: any) {
      console.error("Error adding company:", error);
      toast.error("Failed to add company");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setCompanyName("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-secondary border border-primary/20 text-primary-light max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl text-primary">Add New Company</DialogTitle>
          <DialogDescription className="text-primary-light">
            Enter the name of the new company
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="companyName" className="text-primary-light">Company Name</Label>
            <Input
              id="companyName"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className="border-gray-800 text-black bg-white"
              placeholder="Enter company name"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            className="border-primary/20 text-black hover:bg-transparent hover:text-primary"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleAddCompany}
            disabled={isLoading}
            className="bg-primary hover:bg-primary/90 text-black"
          >
            {isLoading ? "Adding..." : "Add Company"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddCompanyDialog;
